import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, Loader2, Sparkles, Bot, User } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import AIAuditBadge from "@/components/AIAuditBadge";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import KaComparison from "@/components/KaComparison";

const SUGGESTIONS = [
  "Behöver jag bygglov för altan 12 kvm?",
  "Vad är skillnaden mellan bygglov och anmälan?",
  "Får jag bygga Attefallshus 30 kvm utan grannemedgivande?",
  "Vilka handlingar krävs för tillbyggnad?",
  "Hur länge gäller ett bygglov?",
];

function parseSuggestions(text) {
  // Pull follow-ups under "Andra undrar också:" into chips
  const m = text.split(/Andra undrar(?: också)?:?/i);
  if (m.length < 2) return { main: text, followups: [] };
  const followups = m[1].split("\n").map((l) => l.replace(/^[-•*\d.\s]+/, "").trim()).filter((l) => l.length > 3 && l.length < 140);
  return { main: m[0].trim(), followups: followups.slice(0, 4) };
}

export default function ChatAdvisor() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const autoSent = useRef(false);
  const kaShown = useRef(false);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q && !autoSent.current) {
      autoSent.current = true;
      setSearchParams({}, { replace: true });
      send(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId, message: q });
      if (!sessionId) setSessionId(data.session_id);
      trackEvent("ai_fraga_skickad", { source: "chat" });
      const kaMatch = !kaShown.current && (/kontrollansvarig/i.test(q + " " + data.reply) || /\bKA\b/.test(q));
      if (kaMatch) kaShown.current = true;
      setMessages((m) => [...m, { role: "assistant", text: data.reply, audit_id: data.audit_id, showKa: kaMatch }]);
    } catch (err) {
      console.error("AI chat error", err);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let uiMsg = "AI-tjänsten är tillfälligt otillgänglig";
      if (status === 429) {
        uiMsg = typeof detail === "string" ? detail : "För många frågor – försök igen om en stund.";
      } else if (status === 413) {
        uiMsg = "Frågan är för lång. Korta ner den och försök igen.";
      } else if (status === 402) {
        uiMsg = "AI-budgeten är slut just nu. Kom tillbaka senare.";
      } else if (typeof detail === "string" && detail.length < 200) {
        uiMsg = detail;
      }
      toast.error(uiMsg);
      // Ta bort användarens sista meddelande så de kan försöka igen
      setMessages((m) => m.slice(0, -1));
      setInput(q);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up">
      <Seo
        title="AI-rådgivare för bygglov"
        description="Ställ frågor om bygglov, anmälningsplikt, attefall, friggebod, carport, altan, staket och andra åtgärder. Bygglovskompassens AI-rådgivare svarar direkt utan väntetid, helt gratis."
        path="/chat"
        keywords="AI bygglov, bygglovsrådgivning, attefall, friggebod, carport bygglov"
      />
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-blue-700 text-white flex items-center justify-center rounded-sm">
          <Sparkles className="w-6 h-6 stroke-[1.5]" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">AI-rådgivare</div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>Ställ din fråga</h1>
        </div>
      </div>
      <p className="mt-3 text-zinc-600">Ställ frågor om bygglov, PBL, BBR eller kommunala regler. Drivs av Gemini 2.5 Flash.</p>

      {/* Messages */}
      <div className="mt-8 bg-white border border-zinc-200 rounded-sm flex flex-col min-h-[400px]">
        <div className="flex-1 p-4 sm:p-6 space-y-5 max-h-[60vh] overflow-y-auto" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-center py-12 text-zinc-500">
              <Bot className="w-10 h-10 mx-auto text-zinc-300 stroke-[1.5]" />
              <div className="mt-3 text-sm">Hej! Jag svarar på frågor om bygglov, PBL och BBR.</div>
              <div className="text-xs text-zinc-400 mt-1">Välj en föreslagen fråga eller skriv din egen nedan.</div>
            </div>
          )}
          {messages.map((m, i) => {
            const parsed = m.role === "assistant" ? parseSuggestions(m.text) : null;
            return (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  m.role === "user" ? "bg-blue-700 text-white" : "bg-zinc-100 text-zinc-700"
                }`}>
                  {m.role === "user" ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                </div>
                <div className="max-w-[80%]">
                  <div className={`px-4 py-3 rounded-sm text-sm whitespace-pre-line leading-relaxed ${
                    m.role === "user" ? "bg-blue-700 text-white" : "bg-zinc-50 border border-zinc-200 text-zinc-900"
                  }`}>
                    {parsed ? parsed.main : m.text}
                  </div>
                  {parsed?.followups?.length > 0 && (
                    <div className="mt-3" data-testid="followup-suggestions">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Andra undrar också</div>
                      <div className="flex flex-wrap gap-2">
                        {parsed.followups.map((s, j) => (
                          <button
                            key={j}
                            data-testid={`followup-${i}-${j}`}
                            onClick={() => send(s)}
                            className="text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-3 py-1.5 rounded-sm transition"
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {m.role === "assistant" && m.showKa && (
                    <div className="mt-3" data-testid="chat-ka-infocard">
                      <KaComparison compact />
                    </div>
                  )}
                  {m.role === "assistant" && m.audit_id && (
                    <div className="mt-2"><AIAuditBadge auditId={m.audit_id} kind="chat" /></div>
                  )}
                </div>
              </div>
            );
          })}
          {loading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center"><Bot className="w-4 h-4" /></div>
              <div className="px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-sm text-sm text-zinc-500 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> AI:n skriver...
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {messages.length === 0 && (
          <div className="px-4 sm:px-6 pb-4 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={s}
                data-testid={`suggest-${i}`}
                onClick={() => send(s)}
                className="text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-3 py-1.5 rounded-sm transition"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-zinc-200 p-3 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Skriv din fråga om bygglov..."
            data-testid="chat-input"
            className="flex-1 px-4 py-3 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
            disabled={loading}
          />
          <button type="submit" data-testid="chat-send" disabled={loading || !input.trim()}
            className="bg-blue-700 hover:bg-blue-800 disabled:opacity-40 text-white px-4 rounded-sm flex items-center gap-2">
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>

      <p className="mt-4 text-xs text-zinc-500 text-center">
        Svaren är AI-genererade och vägledande. Kommunens beslut är alltid styrande.
      </p>
    </div>
  );
}
